import ExcelJS from 'exceljs';
import { TableColumnConfig, JsonColumnConfig } from '../../tableMain';

interface ExcelDownloadOptions {
    data: Record<string, any>[];
    effectiveColumnConfigs: TableColumnConfig[];
    jsonColumnConfig?: JsonColumnConfig[];
    tableName: string;
    rowColor?: Array<{condition: (rowData: any) => boolean; color: string}>; // Adds a background color to the rows
    userData?: any;
}

export const downloadTableAsExcel = async ({
    data,
    effectiveColumnConfigs,
    jsonColumnConfig,
    tableName,
    rowColor,
    userData,
}: ExcelDownloadOptions) => {
    try {
        // Create a new workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Data');

        // Define columns
        worksheet.columns = effectiveColumnConfigs.map(config => ({
            header: config.colHeader || config.colName,
            key: config.colName,
            width: Math.max((config.colHeader || config.colName).length, 15),
        }));

        // Add data rows
        data.forEach((row, rowIndex) => {
            const excelRow: Record<string, any> = {};
            
            effectiveColumnConfigs.forEach(config => {
                let cellValue = row[config.colName];
                
                // Apply conditionalDisplay if defined
                if (config.conditionalDisplay && userData) {
                    cellValue = config.conditionalDisplay(cellValue, row, userData);
                }
                
                if (cellValue === null || cellValue === undefined || cellValue === '') {
                    excelRow[config.colName] = '';
                } else if (config.colType === 'json') {
                    // For JSON columns, show the actual JSON content as string
                    if (typeof cellValue === 'string') {
                        excelRow[config.colName] = cellValue;
                    } else {
                        excelRow[config.colName] = JSON.stringify(cellValue);
                    }
                } else if (config.colType === 'calculated' || config.colType === 'number') {
                    // If it's a censored string, use as-is
                    if (typeof cellValue === 'string' && isNaN(Number(cellValue))) {
                        excelRow[config.colName] = cellValue;
                    } else {
                        // Ensure numbers are treated as numbers in Excel
                        const numValue = Number(cellValue);
                        excelRow[config.colName] = isNaN(numValue) ? cellValue : numValue;
                    }
                } else if (config.colType === 'boolean') {
                    // Convert boolean to readable text
                    let displayValue = '';

                    if (cellValue === 1) {
                        displayValue = 'Yes';
                    } else if (cellValue === 0 && config.noAsNull === true) {
                        displayValue = '';
                    } else {
                        displayValue = 'No';
                    }
                    excelRow[config.colName] = displayValue;
                } else {
                    // Text columns
                    excelRow[config.colName] = String(cellValue);
                }
            });
            
            const addedRow = worksheet.addRow(excelRow);

            // Apply row color if there's a matching rule
            if (rowColor && rowColor.length > 0) {
                const colorRule = rowColor.find(rule => {
                    try {
                        return rule.condition(row);
                    } catch (e) {
                        return false;
                    }
                });

                if (colorRule) {
                    const excelColor = convertCssToExcelColor(colorRule.color);
                    
                    // Apply background color to all cells in the row
                    addedRow.eachCell((cell) => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: excelColor }
                        };
                    });
                }
            }

            // Apply cell-specific colors (overrides row color if both exist)
            effectiveColumnConfigs.forEach((config, colIndex) => {
                if (config.cellColor && config.cellColor.length > 0) {
                    const cellColorRule = config.cellColor.find(rule => {
                        try {
                            return rule.condition(row);
                        } catch (e) {
                            return false;
                        }
                    });

                    if (cellColorRule) {
                        const cell = addedRow.getCell(colIndex + 1);
                        const excelColor = convertCssToExcelColor(cellColorRule.color);
                        
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: excelColor }
                        };
                    }
                }
            });
        });

        // Apply number format to number columns
        effectiveColumnConfigs.forEach((config, colIndex) => {
            if (config.colType === 'number' || config.colType === 'calculated') {
                const column = worksheet.getColumn(colIndex + 1);
                column.eachCell((cell, rowNumber) => {
                    if (rowNumber > 1) { // Skip header row
                        cell.numFmt = '0.00';
                    }
                });
            }
        });

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
        const filename = `${tableName}_${timestamp}.xlsx`;

        // Write to buffer and download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);

        console.log(`Table downloaded as ${filename}`);
        return true;
    } catch (error) {
        console.error('Error downloading table:', error);
        return false;
    }
};

// Helper function to convert CSS color (hex or var(--custom-property)) to Excel ARGB format
const convertCssToExcelColor = (cssColor: string): string => {
    // Handle CSS custom properties like var(--color-blue-light)
    if (cssColor.startsWith('var(')) {
        const varName = cssColor.match(/var\((--[\w-]+)\)/)?.[1];
        if (varName) {
            // Get the actual color value from CSS custom property
            const actualColor = getComputedStyle(document.documentElement)
                .getPropertyValue(varName)
                .trim();
            
            if (actualColor) {
                cssColor = actualColor;
            }
        }
    }
    
    // Handle hex colors - ExcelJS needs ARGB format (add FF for full opacity)
    if (cssColor.startsWith('#')) {
        return 'FF' + cssColor.slice(1).toUpperCase();
    }
    
    // Handle rgb/rgba colors
    if (cssColor.startsWith('rgb')) {
        const match = cssColor.match(/\d+/g);
        if (match && match.length >= 3) {
            const r = parseInt(match[0]).toString(16).padStart(2, '0');
            const g = parseInt(match[1]).toString(16).padStart(2, '0');
            const b = parseInt(match[2]).toString(16).padStart(2, '0');
            return 'FF' + (r + g + b).toUpperCase();
        }
    }
    
    // Default fallback (white)
    return 'FFFFFFFF';
};